import { useCallback, useEffect, useRef } from 'react'
import { useSelectedModel } from '../../../context/SelectedModelContext'
import type { Model } from '../../../data/models'
import type { GenerationCard } from '../../../types/generationCard'

interface UseCardRecreateOptions {
	/** ID текущей выбранной модели — зависимость эффекта */
	selectedModelId: string | undefined
	/** Все доступные модели — для поиска модели по card.modelId */
	widgetModels: Model[]
	/** Переключить модель (разная реализация для Generate и Widget) */
	switchModel: (model: Model) => void
	/** Применить промпт + настройки + медиа из карточки */
	applyCard: (card: GenerationCard) => Promise<void>
	/** Вызывается при обычной смене модели без ожидающей карточки */
	onModelReset: () => void
}

interface UseCardRecreateReturn {
	/** Воссоздать/применить карточку — передаётся в CardGallery, StylesSection и т.д. */
	handleCardAction: (card: GenerationCard) => Promise<void>
	/**
	 * Инициирует смену модели с отложенной карточкой в очереди.
	 * Используется в handleCreateVideo когда нужна другая модель.
	 */
	triggerRecreate: (card: GenerationCard, model: Model) => void
}

/**
 * Общая логика воссоздания/применения карточек стилей и генераций.
 * Инкапсулирует три ref'а + useEffect на смену модели + handleCardAction.
 *
 * Используется в Generate.tsx и GenerateWidget.tsx.
 */
export function useCardRecreate({
	selectedModelId,
	widgetModels,
	switchModel,
	applyCard,
	onModelReset,
}: UseCardRecreateOptions): UseCardRecreateReturn {
	// Канал «пресет поверх сброса»: model-sync в useGenerateSettings живёт в
	// GenerateProvider (родитель) и на коммите смены модели исполняется ПОЗЖЕ
	// нашего applyCard (эффект потомка) — сбрасывая quality/resolution/duration
	// карточки к дефолтам модели (цена каталога). pendingSettings-эффект
	// провайдера зарегистрирован после model-sync — он вернёт настройки
	// карточки поверх сброса (тот же механизм, что у карточек главной).
	const { setPendingSettings, setPendingModelId } = useSelectedModel()
	const isFirstMount = useRef(true)
	const isRecreatingRef = useRef(false)
	const pendingRecreateRef = useRef<GenerationCard | null>(null)
	const applyCardRef = useRef(applyCard)
	useEffect(() => {
		applyCardRef.current = applyCard
	}, [applyCard])

	// При смене модели: если ждём карточку — применяем её, иначе сбрасываем результат
	useEffect(() => {
		if (isFirstMount.current) {
			isFirstMount.current = false
			return
		}
		if (isRecreatingRef.current) {
			isRecreatingRef.current = false
			const item = pendingRecreateRef.current
			pendingRecreateRef.current = null
			if (item) {
				applyCardRef.current(item)
				console.log('[useCardRecreate] after applyCard, about to check model')
			}
			return
		}
		// Обычная смена модели пользователем
		onModelReset()
	}, [selectedModelId]) // eslint-disable-line react-hooks/exhaustive-deps

	// Ставит карточку в очередь и инициирует смену модели
	const triggerRecreate = useCallback((card: GenerationCard, model: Model) => {
		isRecreatingRef.current = true
		pendingRecreateRef.current = card
		switchModel(model)
	}, [switchModel])

	// Воссоздать/применить карточку: если нужна другая модель — переключаем
	const handleCardAction = useCallback(
		async (card: GenerationCard) => {
			const model = widgetModels.find(
				(m) => m.id === card.modelId || m.backendModel === card.modelId,
			)
			if (model && model.id !== selectedModelId) {
				if (card.settings && Object.keys(card.settings).length) {
					setPendingSettings(card.settings)
					setPendingModelId(card.modelId ?? null)
				}
				triggerRecreate(card, model)
				return
			}
			await applyCardRef.current(card)
		},
		[selectedModelId, widgetModels, triggerRecreate, setPendingSettings, setPendingModelId],
	)

	return { handleCardAction, triggerRecreate }
}
